import { Badge } from '@/components/ui/badge';
import { Info, Puzzle } from 'lucide-react';
import type { ReactElement } from 'react';
import type { ExtensionCapability } from '@kirin/api-client/contracts/analyses';

interface CapabilitySummaryProps {
  capabilities: ExtensionCapability[];
  className?: string;
}

/**
 * Displays detected capabilities for an extension in a non-alert style.
 * Capabilities are informational signals (info/low severity) that provide
 * visibility into what an extension can do without raising the risk level.
 *
 * Used alongside findings in extension detail / alert expansion views.
 */
export function CapabilitySummary({
  capabilities,
  className,
}: CapabilitySummaryProps): ReactElement | null {
  if (capabilities.length === 0) return null;

  // Group by category
  const grouped = capabilities.reduce<Record<string, ExtensionCapability[]>>(
    (acc, cap) => {
      const key = cap.category;
      if (!acc[key]) acc[key] = [];
      acc[key].push(cap);
      return acc;
    },
    {}
  );

  const categoryLabels: Record<string, string> = {
    'ai-ide': 'AI & IDE Integration',
    activation: 'Activation Patterns',
    'supply-chain': 'Supply Chain',
    runtime: 'Runtime',
    notebook: 'Notebook',
    custom: 'Custom Rules',
    'credential-access': 'Credential Access',
    'credential-targeting': 'Credential Targeting',
    debugger: 'Debugger',
    'code-execution': 'Code Execution',
    'custom-editor': 'Custom Editor',
    filesystem: 'File System',
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-3">
        <Puzzle className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Detected Capabilities</span>
        <Badge variant="secondary" className="text-[10px]">
          {capabilities.length}
        </Badge>
      </div>

      <div className="space-y-3">
        {Object.entries(grouped).map(([category, caps]) => (
          <div key={category} className="space-y-1.5">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {categoryLabels[category] ?? category}
            </span>

            <div className="space-y-1">
              {caps.map((cap, idx) => (
                <div
                  key={`${cap.ruleId}-${idx}`}
                  className="flex items-start gap-2 rounded-md border border-border/50 bg-muted/30 px-3 py-2"
                >
                  <Info className="mt-0.5 h-3 w-3 shrink-0 text-blue-500/70" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-foreground/90 leading-relaxed">
                      {cap.description}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <code className="text-[10px] bg-muted px-1 py-0.5 rounded text-muted-foreground">
                        {cap.field}
                      </code>
                      {cap.matchedValue != null && (
                        <span className="text-[10px] text-muted-foreground">
                          = {String(cap.matchedValue)}
                        </span>
                      )}
                      <Badge variant="outline" className="text-[9px] h-4 px-1">
                        {cap.severity}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
